// Servicio de estado de red — fuente única de verdad para:
//
//  - Si el usuario forzó modo offline manual (decidió no usar la red por
//    estar lenta).
//  - Latencia medida real de las últimas peticiones a la API.
//  - Si hay alguna petición en curso ahora mismo.
//
// El modo offline manual NO es lo mismo que `!navigator.onLine`:
//
//  - `!navigator.onLine` → no hay red física disponible.
//  - `userOfflineMode === true` → hay red pero el usuario eligió no usarla
//    porque era lenta.
//
// La unión de los dos es lo que las pantallas deben usar para decidir si
// encolar o llamar a la API. Para eso exportamos `isOfflineForUser()`.
//
// Ningún componente debe acceder a estas globales directamente — todos
// pasan por `useNetworkStatus()`, `setUserOffline()` o `isOfflineForUser()`.

import { useSyncExternalStore } from 'react';
import { track } from './analytics';

// ── Estado interno ─────────────────────────────────────────────────────────

let userOfflineMode = false;
// Reloj del modo offline manual: instante desde el que se cuenta su caducidad.
// No es siempre el momento de pulsar el botón — si cuando lo pulsó no había
// nada en cola, el reloj se reinicia cuando aparece el primer pendiente (ver
// touchManualOfflineClock). null = no estamos en modo manual.
let manualOfflineSince: number | null = null;
let latencies: number[] = []; // ms, máximo 5 más recientes
let inflightStartedAt: number | null = null; // timestamp ms o null
// Nº de peticiones seguidas SIN respuesta (timeout / red caída / lie-fi). Al
// llegar al umbral NO se avisa todavía: se lanza una sonda (una sola petición
// ligera, fuera de la cola de axios y de la caché del SW) y solo si ella
// tampoco obtiene respuesta se confirma 'offline'. La primera respuesta lo
// resetea todo.
//
// Por qué la sonda (sep 2026): al abrir la app se lanzan ~20 peticiones a la
// vez; el navegador solo atiende 6 por servidor y el resto espera en cola con el
// reloj de 10 s de axios corriendo. Dos de ellas agotaban el plazo en cola y la
// cabecera decía "Sin conexión" con la red perfecta.
let unreachableFailures = 0;
// La sonda confirmó que el servidor no responde: este es el único 'offline'
// con red física. Se reintenta sola cada PROBE_RETRY_MS hasta que vuelva.
let unreachableConfirmed = false;
let probing = false;
let reprobeTimer: ReturnType<typeof setTimeout> | null = null;
let reachabilityProbe: (() => Promise<boolean>) | null = null;
// "Época" de conexión: empieza al arrancar la app y otra vez al volver de estar
// oculta un rato (el móvil congela la pestaña y al despertar la radio y las
// conexiones están frías). Hasta la primera respuesta del servidor en la época
// el estado es 'connecting', y las peticiones lanzadas antes de esa respuesta
// no cuentan para la latencia: miden el arranque y la cola, no la red.
let epochStartedAt = Date.now();
let connectedInEpoch = false;
let hiddenSince: number | null = null;
// Momento de la última respuesta REAL del backend (aunque fuera un error HTTP:
// lo que importa es que hubo diálogo). Es la prueba de red más honesta que
// tenemos, y la usa la caducidad del modo offline manual para decidir si la
// vuelta a online se sostiene. null = aún no ha respondido nadie.
let lastServerResponseAt: number | null = null;
const activeAborts = new Set<AbortController>();
const subscribers = new Set<() => void>();

// ── Constantes ─────────────────────────────────────────────────────────────

const MAX_LATENCY_SAMPLES = 5;

// Umbrales de clasificación basados en la MEDIANA de latencias: una petición
// suelta muy lenta (una foto, un informe) no pinta la conexión de lenta.
const FAST_THRESHOLD_MS = 1000;
const SLOW_THRESHOLD_MS = 3000;
// Mínimo de muestras para declarar 'slow': con 1-2 medidas no hay evidencia.
const MIN_SLOW_SAMPLES = 3;

// Fallos de red seguidos que marcan el servidor como inalcanzable (lie-fi). 2
// evita falsos positivos por un timeout puntual pero avisa rápido si la red no
// tiene salida real.
const UNREACHABLE_THRESHOLD = 2;

// Reintento de la sonda mientras está confirmado 'offline' con red física.
const PROBE_RETRY_MS = 15000;
// Tiempo oculta a partir del cual la vuelta abre una época nueva.
const RESUME_GAP_MS = 30000;

// ── Tipos ──────────────────────────────────────────────────────────────────

export type NetworkStatus =
  | { kind: 'fast'; latencyMs: number | null }
  | { kind: 'normal'; latencyMs: number | null }
  | { kind: 'slow'; latencyMs: number | null }
  | { kind: 'connecting'; latencyMs: null }
  | { kind: 'manual_offline'; latencyMs: null }
  | { kind: 'offline'; latencyMs: null };

// ── Helpers internos ───────────────────────────────────────────────────────

function medianLatency(): number | null {
  if (latencies.length === 0) return null;
  const sorted = [...latencies].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return Math.round(sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2);
}

function isSlowByLatency(): boolean {
  const lat = medianLatency();
  return lat != null && latencies.length >= MIN_SLOW_SAMPLES && lat >= SLOW_THRESHOLD_MS;
}

// Mantenemos el último estado emitido para evitar telemetría redundante.
let lastTrackedKind: NetworkStatus['kind'] | null = null;

function notify() {
  // Telemetría: emitir solo cuando el `kind` cambia (no en cada update de
  // latencia o de inflight).
  const current = getNetworkStatus();
  if (lastTrackedKind !== current.kind) {
    lastTrackedKind = current.kind;
    track({ name: 'coverage_changed', params: { level: current.kind } });
  }
  subscribers.forEach((cb) => {
    try { cb(); } catch { /* ignoramos subscribers rotos */ }
  });
}

// ── API pública ────────────────────────────────────────────────────────────

// Cacheamos el último snapshot devuelto por getNetworkStatus para que
// useSyncExternalStore lo compare con Object.is y NO re-renderice si nada
// cambió. Si devolviéramos un objeto nuevo en cada llamada, React entraría
// en bucle infinito (error #185 — Maximum update depth exceeded).
let cachedSnapshot: NetworkStatus | null = null;

function isServerUnreachable(): boolean {
  return unreachableConfirmed;
}

function clearReprobe() {
  if (reprobeTimer != null) {
    clearTimeout(reprobeTimer);
    reprobeTimer = null;
  }
}

// La registra api.ts: una petición ligera que no pasa por la cola de axios ni
// por la caché del SW. Devuelve true si el servidor contestó (lo que sea).
export function setReachabilityProbe(probe: (() => Promise<boolean>) | null): void {
  reachabilityProbe = probe;
}

async function runProbe(): Promise<void> {
  if (probing) return;
  // Sin sonda registrada (tests, arranque temprano) se conserva el criterio
  // antiguo: el umbral de fallos basta para confirmar.
  if (!reachabilityProbe) {
    unreachableConfirmed = true;
    notify();
    return;
  }
  probing = true;
  clearReprobe();
  notify();
  let ok: boolean;
  try {
    ok = await reachabilityProbe();
  } catch {
    ok = false;
  }
  probing = false;
  if (ok) {
    reportRequestOutcome(true);
    notify();
    return;
  }
  unreachableConfirmed = true;
  notify();
  scheduleReprobe();
}

function scheduleReprobe() {
  clearReprobe();
  if (!unreachableConfirmed) return;
  reprobeTimer = setTimeout(() => {
    reprobeTimer = null;
    // Solo con la app a la vista y red física: si no, los eventos online /
    // visibilitychange ya abren una época nueva al volver.
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    if (unreachableConfirmed && navigator.onLine && !userOfflineMode && visible) {
      void runProbe();
    } else if (unreachableConfirmed) {
      scheduleReprobe();
    }
  }, PROBE_RETRY_MS);
}

// Reportado por el interceptor de axios al terminar cada petición: true si
// llegó respuesta del servidor (hay conexión real, aunque sea un error HTTP),
// false si no hubo respuesta (timeout / red caída / lie-fi). Basar el aviso en
// esto —y no solo en navigator.onLine— es lo que permite detectar el lie-fi.
// `startedAt`: si la petición salió antes de la época actual (la pestaña estuvo
// congelada con ella en vuelo), su fallo no dice nada de la red de ahora.
export function reportRequestOutcome(reachedServer: boolean, startedAt?: number): void {
  if (reachedServer) {
    lastServerResponseAt = Date.now();
    const changed = unreachableFailures !== 0 || unreachableConfirmed || !connectedInEpoch;
    unreachableFailures = 0;
    unreachableConfirmed = false;
    connectedInEpoch = true;
    clearReprobe();
    if (changed) notify();
    return;
  }
  if (startedAt != null && startedAt < epochStartedAt) return;
  if (unreachableConfirmed || probing) return;
  unreachableFailures++;
  // Sin red física no hace falta sonda: navigator.onLine ya pinta 'offline' y
  // el evento 'online' abrirá una época nueva.
  if (unreachableFailures >= UNREACHABLE_THRESHOLD && navigator.onLine) void runProbe();
  else notify();
}

// Abre una época nueva: al volver de estar oculta un rato o al recuperar red.
// Olvida las medidas de antes (eran de otra red / otra situación).
export function startConnectionEpoch(now: number = Date.now()): void {
  epochStartedAt = now;
  connectedInEpoch = false;
  latencies = [];
  unreachableFailures = 0;
  unreachableConfirmed = false;
  clearReprobe();
  notify();
}

function computeStatus(): NetworkStatus {
  if (!navigator.onLine) return { kind: 'offline', latencyMs: null };
  if (userOfflineMode) return { kind: 'manual_offline', latencyMs: null };
  // Lie-fi CONFIRMADO por la sonda: el SO dice que hay red pero el servidor no
  // responde. Es el único 'offline' con navigator.onLine a true.
  if (isServerUnreachable()) return { kind: 'offline', latencyMs: null };
  // Comprobando (sonda en vuelo) o esperando la primera respuesta de la época.
  if (probing) return { kind: 'connecting', latencyMs: null };
  if (!connectedInEpoch && (inflightStartedAt != null || unreachableFailures > 0)) {
    return { kind: 'connecting', latencyMs: null };
  }
  const lat = medianLatency();
  if (lat == null) return { kind: 'normal', latencyMs: null };
  if (isSlowByLatency()) return { kind: 'slow', latencyMs: lat };
  if (lat >= FAST_THRESHOLD_MS) return { kind: 'normal', latencyMs: lat };
  return { kind: 'fast', latencyMs: lat };
}

export function getNetworkStatus(): NetworkStatus {
  const next = computeStatus();
  if (
    cachedSnapshot &&
    cachedSnapshot.kind === next.kind &&
    cachedSnapshot.latencyMs === next.latencyMs
  ) {
    return cachedSnapshot;
  }
  cachedSnapshot = next;
  return next;
}

// Descripción de la cobertura para la telemetría — MISMO campo 'cobertura' que
// envía la app Android, para que el visor pinte las dos igual.
// Combina lo que hay disponible, de más a menos fiable:
//  1. navigator.onLine === false → 'SIN RED'.
//  2. Nuestras mediciones (media de latencias reales contra la API, o servidor
//     inalcanzable) → BUENA / DEBIL / DEFICIENTE. Funciona en todos los
//     navegadores, también Safari y Firefox.
//  3. Network Information API donde exista (Chromium): effectiveType, downlink
//     y rtt como detalle.
// Formatos: "4g 8.5Mbps 120ms BUENA" · "WEB BUENA (620ms)" · "SIN RED".
export function describeCoverage(): string {
  if (!navigator.onLine) return 'SIN RED';
  if (userOfflineMode) return 'OFFLINE MANUAL';
  const status = getNetworkStatus();
  const quality =
    status.kind === 'offline' ? 'DEFICIENTE'   // lie-fi: hay red pero no responde
    : status.kind === 'slow' ? 'DEBIL'
    : status.kind === 'connecting' ? 'CONECTANDO'
    : 'BUENA';
  type NavConn = { effectiveType?: string; downlink?: number; rtt?: number };
  const conn = (navigator as Navigator & { connection?: NavConn }).connection;
  const detail: string[] = [];
  if (conn?.effectiveType) detail.push(conn.effectiveType);
  if (typeof conn?.downlink === 'number' && conn.downlink > 0) detail.push(`${conn.downlink}Mbps`);
  if (typeof conn?.rtt === 'number' && conn.rtt > 0) detail.push(`${conn.rtt}ms`);
  if (detail.length > 0) return `${detail.join(' ')} ${quality}`;
  const lat = status.latencyMs;
  return lat != null ? `WEB ${quality} (${lat}ms)` : `WEB ${quality}`;
}

// ¿Conexión MALA ahora mismo? Decide el presupuesto del guardado en primer
// plano (10s en vez de 25s). Combina dos fuentes, de más a menos disponible:
//  1. Nuestras propias mediciones: mediana de latencias ≥ SLOW_THRESHOLD_MS, o
//     el servidor marcado como inalcanzable (lie-fi). Funciona en todos los
//     navegadores, incluidos Safari y Firefox.
//  2. Network Information API donde exista (Chromium): effectiveType
//     'slow-2g'/'2g' o downlink < 1 Mbps. Feature-detect, jamás asumido.
// Sin ninguna señal = se asume buena: mejor pecar de presupuesto largo que
// recortar a ciegas el tiempo de un guardado que iba a salir bien.
export function isPoorConnection(): boolean {
  type NavConn = { effectiveType?: string; downlink?: number };
  const conn = (navigator as Navigator & { connection?: NavConn }).connection;
  if (conn) {
    const et = (conn.effectiveType ?? '').toLowerCase();
    if (et === 'slow-2g' || et === '2g') return true;
    if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < 1) return true;
  }
  if (isServerUnreachable()) return true;
  return isSlowByLatency();
}

// "Sin red real" o "modo offline manual": la app debe encolar en lugar de
// llamar a la API. Las pantallas usan este helper en su decisión inicial.
export function isOfflineForUser(): boolean {
  return !navigator.onLine || userOfflineMode;
}

export function isUserOfflineMode(): boolean {
  return userOfflineMode;
}

// Activa o desactiva el modo offline manual. Cuando se activa:
//   - Aborta todas las peticiones HTTP en curso (para que sus promesas se
//     resuelvan ya y los componentes caigan a su catch → caché local).
//   - Limpia el contador de inflight (ya no hay nada esperando).
// La elección del usuario se respeta hasta que él mismo pulse "Volver a
// intentar online", se dispare el evento `online` del SO tras una transición
// real offline→online, o CADUQUE el modo por llevar demasiado tiempo activo
// con trabajo pendiente (isManualOfflineExpired — la decisión y el plazo son
// del coordinador de la cola, aquí solo se lleva el reloj).
export function setUserOfflineMode(value: boolean): void {
  if (userOfflineMode === value) return;
  userOfflineMode = value;
  manualOfflineSince = value ? Date.now() : null;
  if (value) {
    // Cancelar todo lo que esté esperando respuesta.
    activeAborts.forEach((c) => {
      try { c.abort(); } catch { /* ya abortado */ }
    });
    activeAborts.clear();
    inflightStartedAt = null;
    // El usuario eligió offline: no arrastramos el contador de inalcanzable.
    unreachableFailures = 0;
    unreachableConfirmed = false;
    clearReprobe();
  }
  notify();
}

// Última vez que el backend respondió algo (ms epoch), o null si nunca.
// Comparado contra un instante previo, responde a "¿ha habido red DESPUÉS de
// aquel momento?" sin tener que interpretar los estados de la cola.
export function getLastServerResponseAt(): number | null {
  return lastServerResponseAt;
}

// Reinicia el reloj de caducidad sin tocar el modo. Lo llama la cola cuando
// aparece el PRIMER pendiente estando ya en offline manual: el plazo debe
// contar desde que hay trabajo atrapado, no desde que se pulsó el botón. Sin
// esto, un modo activado hace horas caducaría en el mismo instante de guardar
// la carga — justo cuando el usuario menos espera volver a la red.
export function touchManualOfflineClock(): void {
  if (userOfflineMode) manualOfflineSince = Date.now();
}

// ¿Lleva el modo offline manual `ttlMs` o más activo? Consulta pura, sin
// efectos: qué hacer al caducar lo decide useOfflineSync.
export function isManualOfflineExpired(ttlMs: number, now: number = Date.now()): boolean {
  if (!userOfflineMode || manualOfflineSince == null) return false;
  return now - manualOfflineSince >= ttlMs;
}

// Llamado por el interceptor de axios al iniciar cada petición.
// Devuelve un AbortController cuya señal debe pasarse a la petición, y un
// callback `done` que la pantalla debe invocar al terminar (éxito o error)
// con la latencia en ms.
export function trackRequestStart(): { controller: AbortController; done: (latencyMs?: number) => void } {
  const controller = new AbortController();
  activeAborts.add(controller);
  const startedAt = Date.now();
  // Solo miden la red las peticiones que salen con la conexión ya establecida
  // en esta época: las de antes incluyen arranque de radio/TLS y cola.
  const measurable = connectedInEpoch && startedAt >= epochStartedAt;
  // Solo el primer inflight marca el comienzo del "loading global" — si hay
  // varias peticiones a la vez, contamos desde la primera.
  if (inflightStartedAt == null) {
    inflightStartedAt = startedAt;
    notify();
  }
  return {
    controller,
    done: (latencyMs?: number) => {
      activeAborts.delete(controller);
      if (measurable && typeof latencyMs === 'number' && latencyMs >= 0) {
        latencies.push(latencyMs);
        if (latencies.length > MAX_LATENCY_SAMPLES) {
          latencies = latencies.slice(-MAX_LATENCY_SAMPLES);
        }
      }
      // Si no quedan peticiones activas, limpiamos el inflight.
      if (activeAborts.size === 0) {
        inflightStartedAt = null;
      }
      notify();
    },
  };
}

export function getInflightStartedAt(): number | null {
  return inflightStartedAt;
}

// ── Suscripción ────────────────────────────────────────────────────────────

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// El navegador puede cambiar navigator.onLine y nos enteramos vía evento.
// Reemitimos `notify` para que el indicador y el banner reaccionen.
if (typeof window !== 'undefined') {
  // Al recuperar red física damos otra oportunidad: época nueva, la próxima
  // petición decide el estado desde cero.
  window.addEventListener('online', () => startConnectionEpoch());
  window.addEventListener('offline', notify);
  // Vuelta desde la caché de navegación (bfcache): conexiones frías.
  window.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) startConnectionEpoch();
  });
}
if (typeof document !== 'undefined') {
  // Vuelta de la pestaña tras un rato oculta (pantalla apagada, otra app): el
  // navegador la congeló y sus conexiones están frías. Época nueva.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
      return;
    }
    const wasHiddenFor = hiddenSince != null ? Date.now() - hiddenSince : 0;
    hiddenSince = null;
    if (wasHiddenFor >= RESUME_GAP_MS) startConnectionEpoch();
  });
}

// Hook React: devuelve el estado agregado y se re-renderiza cuando cambia.
export function useNetworkStatus(): NetworkStatus {
  return useSyncExternalStore(subscribe, getNetworkStatus, getNetworkStatus);
}

// Hook auxiliar: devuelve el ms desde que arrancó la petición actual en
// curso, o null si no hay ninguna. El banner de carga lenta lo usa.
export function useInflightSince(): number | null {
  return useSyncExternalStore(subscribe, getInflightStartedAt, () => null);
}
